import React, { isValidElement, useEffect, useState, useRef } from 'react';

const severityClass = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'danger',
};

const severityIconClass = {
  info: 'fa-info-circle',
  success: 'fa-check-circle',
  warning: 'fa-exclamation-triangle',
  danger: 'fa-times-circle',
};

const resolveNotificationContent = ({ notification, onDismiss, ref }) => {
  if (!notification) {
    return null;
  }

  if (isValidElement(notification.content)) {
    // Pass the unique ID to the cloned element so the timer can reset
    return React.cloneElement(notification.content, { onDismiss, ref, toastId: notification.id });
  }

  console.warn('KskToast recebeu notification.content que não é ReactElement; usando fallback textual.', {
    notification,
  });

  return {
    info: notification.type === 'info',
    success: notification.type === 'success',
    warning: notification.type === 'warning',
    error: notification.type === 'error',
    mainMessage: typeof notification.content === 'string' || typeof notification.content === 'number'
      ? `${notification.content}`
      : 'Mensagem indisponível',
  };
};

function useToastTimer(isOuter, onDismiss, currentId, durationMs = 5000, showTimer = true) {
  const progressBarRef = useRef(null);
  const isHovered = useRef(false);
  const lastTime = useRef(performance.now());
  const progressRef = useRef(0);

  useEffect(() => {
    if (isOuter || !onDismiss || !showTimer) return;
    
    let animationFrameId;
    
    // Reset timer state when a new toast is shown
    progressRef.current = 0;
    if (progressBarRef.current) {
      progressBarRef.current.style.width = '0%';
    }
    isHovered.current = false;
    lastTime.current = performance.now();

    const animate = (time) => {
      const delta = time - lastTime.current;
      lastTime.current = time;

      if (!isHovered.current) {
        progressRef.current += (delta / durationMs) * 100;
        
        if (progressBarRef.current) {
          progressBarRef.current.style.width = `${progressRef.current}%`;
        }

        if (progressRef.current >= 100) {
          onDismiss();
          return;
        }
      }
      
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isOuter, onDismiss, currentId, durationMs, showTimer]);

  const handleMouseEnter = () => { isHovered.current = true; };
  const handleMouseLeave = () => { 
    isHovered.current = false; 
    lastTime.current = performance.now(); 
  };

  return { progressBarRef, handleMouseEnter, handleMouseLeave };
}

const KskToast = React.forwardRef(({ notification, toastId, httpError, mainMessage, details, messages, info, success, warning, error, onDismiss, showTimer = true }, ref) => {
  const notificationContent = resolveNotificationContent({ notification, onDismiss, ref });
  const isOuter = isValidElement(notificationContent);

  // Determine the current ID to depend on for restarting the timer
  const currentId = notification?.id || toastId;

  const { progressBarRef, handleMouseEnter, handleMouseLeave } = useToastTimer(isOuter, onDismiss, currentId, 5000, showTimer);

  if (isOuter) {
    return notificationContent;
  }

  const fallbackNotificationProps = notificationContent || {};

  const severity = (fallbackNotificationProps.success || success) ? 'success'
    : (fallbackNotificationProps.warning || warning) ? 'warning'
    : (fallbackNotificationProps.error || error) ? 'error'
    : 'info';

  const cssClass = severityClass[severity] || 'info';
  const iconClass = severityIconClass[cssClass] || 'fa-info-circle';

  const { httpMessage, httpMessages, httpDetails } = httpError ? digestError(httpError) : {};
  const toastMessage = fallbackNotificationProps.mainMessage || mainMessage || httpMessage;
  const toastDetails = details || httpDetails;
  const toastMessageList = messages || httpMessages;

  return (
    <div
      ref={ref}
      className={`br-message container-fluid ${cssClass}`}
      style={{
        position: "fixed",
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 6px 0px",
        zIndex: 999,
        paddingBottom: "4px",
        overflow: "hidden",
        cursor: showTimer ? 'pointer' : 'default'
      }}
      onMouseEnter={showTimer ? handleMouseEnter : undefined}
      onMouseLeave={showTimer ? handleMouseLeave : undefined}
    >
      <div className="icon">
        <i className={`fas ${iconClass} fa-lg`} aria-hidden="true"></i>
      </div>
      <div className="content" role="alert">
        {toastMessage && <div className="message-title">{toastMessage}</div>}
        {toastDetails && <div className="message-body">{toastDetails}</div>}
        {toastMessageList && (
          <ul style={{ listStyle: 'square', margin: 0, paddingLeft: '1.5em' }}>
            {toastMessageList.map(m => <li key={m}>{m}</li>)}
          </ul>
        )}
      </div>
      {onDismiss && (
        <div className="close">
          <button
            className="br-button circle small"
            type="button"
            aria-label="Fechar mensagem"
            onClick={onDismiss}
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>
      )}
      {showTimer && (
        <div
          ref={progressBarRef}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: '4px',
            backgroundColor: 'rgba(0,0,0,0.2)',
            width: '0%',
            transition: 'none'
          }}
        ></div>
      )}
    </div>
  );
});

function digestError(httpError = {}) {
  const { isAxiosError, code, response, response: { data: { error, errors, status, message } = {} } = {} } = httpError;

  console.log('HTTP ERROR', { ...httpError });

  const noResponse = isAxiosError && (response === undefined);
  const expired = noResponse && (code === 'ECONNABORTED' || code === 'ETIMEDOUT');
  const offlineMsg = noResponse && !expired && 'Não foi possível conectar ao Siconfi.';
  const expiredMsg = expired && 'O Siconfi demorou para responder.';
  const connectionDetails = noResponse && 'Verifique sua conexão com a internet e tente novamente em alguns instantes. Se o problema persistir, entre em contato com o suporte.';

  return {
    status,
    httpMessage: error || offlineMsg || expiredMsg,
    httpMessages: errors?.map(({ message }) => `${message}`),
    httpDetails: message || connectionDetails,
  };
}

export default KskToast;
